import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { TrendingUp, TrendingDown, ChevronRight, Wallet } from "lucide-react";
import { useBalanceSummary } from "@/application/hooks/useExpenses";

export function BalanceSummaryCard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: summary, isLoading } = useBalanceSummary();

  const formatAmount = (amount: number) => {
    return new Intl.NumberFormat("ja-JP", {
      style: "currency",
      currency: "JPY",
    }).format(amount);
  };

  if (isLoading) {
    return (
      <div className="animate-pulse rounded-xl border bg-card p-4">
        <div className="h-6 w-32 rounded bg-muted" />
        <div className="mt-3 h-10 w-40 rounded bg-muted" />
        <div className="mt-2 h-4 w-48 rounded bg-muted" />
      </div>
    );
  }

  // Don't show if no balance data
  if (!summary || (summary.totalReceivable === 0 && summary.totalPayable === 0)) {
    return null;
  }

  const netBalance = summary.netBalance;
  const isPositive = netBalance > 0;
  const isNegative = netBalance < 0;
  const isEven = netBalance === 0;

  // Compact single-line display
  return (
    <button
      className="flex w-full items-center gap-3 rounded-xl border bg-card px-4 py-3 text-left transition-all hover:bg-accent hover:shadow-sm active:scale-[0.99]"
      onClick={() => navigate("/groups")}
    >
      {/* Icon */}
      <div
        className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${
          isPositive
            ? "bg-green-100 text-green-600 dark:bg-green-900 dark:text-green-400"
            : isNegative
              ? "bg-red-100 text-red-600 dark:bg-red-900 dark:text-red-400"
              : "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400"
        }`}
      >
        {isPositive ? (
          <TrendingUp className="h-5 w-5" />
        ) : isNegative ? (
          <TrendingDown className="h-5 w-5" />
        ) : (
          <Wallet className="h-5 w-5" />
        )}
      </div>

      {/* Content */}
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-2">
          <span className="text-sm text-muted-foreground">{t("home.balanceSummary.title")}</span>
          <span
            className={`text-lg font-bold ${
              isPositive
                ? "text-green-600 dark:text-green-400"
                : isNegative
                  ? "text-red-600 dark:text-red-400"
                  : "text-muted-foreground"
            }`}
          >
            {isEven
              ? t("home.balanceSummary.even")
              : `${isPositive ? "+" : "-"}${formatAmount(Math.abs(netBalance))}`}
          </span>
        </div>
        <p className="text-xs text-muted-foreground">
          {isPositive && t("home.balanceSummary.receivable")}
          {isNegative && t("home.balanceSummary.payable")}
          {isEven && t("home.balanceSummary.noOutstanding")}
          {summary.groups.length > 1 &&
            ` (${t("home.balanceSummary.groupCount", { count: summary.groups.length })})`}
        </p>
      </div>

      {/* Arrow */}
      <ChevronRight className="h-5 w-5 shrink-0 text-muted-foreground" />
    </button>
  );
}
